import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import { listRetailStores, type RetailStoreOption } from './storesApi';
import {
  deleteChannelScope,
  deleteLocationGroup,
  deleteStoreOverride,
  deleteStoreScope,
  listChannelScopes,
  listLocationGroups,
  listStoreOverrides,
  listStoreScopes,
  upsertChannelScope,
  upsertLocationGroup,
  upsertStoreOverride,
  upsertStoreScope,
} from './channelStockApi';
import type {
  ChannelScope,
  ChannelScopeUpsert,
  LocationGroup,
  LocationGroupUpsert,
  StoreOverride,
  StoreOverrideUpsert,
  StoreScope,
  StoreScopeUpsert,
} from '../model/types';

export const channelStockKeys = {
  groups: ['channel-stock', 'groups'] as const,
  locations: ['channel-stock', 'locations'] as const,
  retailStores: ['channel-stock', 'retail-stores'] as const,
  derivation: (storeCode: string, productId: string) =>
    ['channel-stock', 'derivation', storeCode, productId] as const,
  channelScopes: ['channel-stock', 'channel-scopes'] as const,
  storeScopes: ['channel-stock', 'store-scopes'] as const,
  overrides: (storeCode: string) => ['channel-stock', 'overrides', storeCode] as const,
};

export function useRetailStores() {
  return useQuery<RetailStoreOption[], NormalizedApiError>({
    queryKey: channelStockKeys.retailStores,
    queryFn: listRetailStores,
  });
}

export function useLocationGroups() {
  return useQuery<LocationGroup[], NormalizedApiError>({
    queryKey: channelStockKeys.groups,
    queryFn: listLocationGroups,
  });
}

export function useChannelScopes() {
  return useQuery<ChannelScope[], NormalizedApiError>({
    queryKey: channelStockKeys.channelScopes,
    queryFn: listChannelScopes,
  });
}

export function useStoreScopes() {
  return useQuery<StoreScope[], NormalizedApiError>({
    queryKey: channelStockKeys.storeScopes,
    queryFn: listStoreScopes,
  });
}

export function useStoreOverrides(storeCode: string | null) {
  return useQuery<StoreOverride[], NormalizedApiError>({
    queryKey: channelStockKeys.overrides(storeCode ?? ''),
    queryFn: () => listStoreOverrides(storeCode as string),
    enabled: Boolean(storeCode),
  });
}

export function useUpsertLocationGroup() {
  const queryClient = useQueryClient();
  return useMutation<LocationGroup, NormalizedApiError, LocationGroupUpsert>({
    mutationFn: upsertLocationGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: channelStockKeys.groups });
      // membership feeds every bound scope's pool — refresh dependants too
      void queryClient.invalidateQueries({ queryKey: channelStockKeys.channelScopes });
      void queryClient.invalidateQueries({ queryKey: channelStockKeys.storeScopes });
    },
  });
}

export function useDeleteLocationGroup() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: deleteLocationGroup,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelStockKeys.groups }),
  });
}

export function useUpsertChannelScope() {
  const queryClient = useQueryClient();
  return useMutation<ChannelScope, NormalizedApiError, { channelCode: string; body: ChannelScopeUpsert }>({
    mutationFn: ({ channelCode, body }) => upsertChannelScope(channelCode, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelStockKeys.channelScopes }),
  });
}

export function useDeleteChannelScope() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: deleteChannelScope,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelStockKeys.channelScopes }),
  });
}

export function useUpsertStoreScope() {
  const queryClient = useQueryClient();
  return useMutation<StoreScope, NormalizedApiError, { storeCode: string; body: StoreScopeUpsert }>({
    mutationFn: ({ storeCode, body }) => upsertStoreScope(storeCode, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelStockKeys.storeScopes }),
  });
}

export function useDeleteStoreScope() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: deleteStoreScope,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelStockKeys.storeScopes }),
  });
}

export function useUpsertStoreOverride() {
  const queryClient = useQueryClient();
  return useMutation<
    StoreOverride,
    NormalizedApiError,
    { storeCode: string; productId: string; body: StoreOverrideUpsert }
  >({
    mutationFn: ({ storeCode, productId, body }) => upsertStoreOverride(storeCode, productId, body),
    onSuccess: (_data, variables) =>
      void queryClient.invalidateQueries({ queryKey: channelStockKeys.overrides(variables.storeCode) }),
  });
}

export function useDeleteStoreOverride() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, { storeCode: string; productId: string }>({
    mutationFn: ({ storeCode, productId }) => deleteStoreOverride(storeCode, productId),
    onSuccess: (_data, variables) =>
      void queryClient.invalidateQueries({ queryKey: channelStockKeys.overrides(variables.storeCode) }),
  });
}
